import { useEffect, useState } from "react";
import { useMutation } from "@apollo/client";
import {
  UPDATE_AUDIT_CONTROL_STATUS,
  GET_AUDIT_FRAMEWORK_MIDDLE_PANEL
} from "../../../../apis/settingsApis/audit-apis/auditApis";
import { AuditorStatusType, AdminStatusType } from "../../../../utils/enums";

export const useControlStatus = (control, audit, isAuditorView = false, setSnackbarInfo = null) => {
  const [isSubmittingStatus, setIsSubmittingStatus] = useState(false);
  const [isSubmittingRedFlag, setIsSubmittingRedFlag] = useState(false);

  const getCurrentStatus = () => {
    return control?.status || null;
  };

  const getRedFlag = () => {
    return control?.redFlag || false;
  };

  const [currentStatusKey, setCurrentStatusKey] = useState(getCurrentStatus());
  const [redFlag, setRedFlag] = useState(getRedFlag());

  useEffect(() => {
    setCurrentStatusKey(getCurrentStatus());
    setRedFlag(getRedFlag());
  }, [control?.status, control?.redFlag]);

  const [updateControlStatus] = useMutation(UPDATE_AUDIT_CONTROL_STATUS, {
    refetchQueries: [
      {
        query: GET_AUDIT_FRAMEWORK_MIDDLE_PANEL,
        variables: { audit_id: audit?.id },
      },
    ],
    awaitRefetchQueries: true,
  });

  const handleStatusUpdate = async (statusKey) => {
    if (!control?.auditControlId) return;
    setIsSubmittingStatus(true);

    const originalStatus = getCurrentStatus();

    try {
      const variables = {
        audit_control_id: control.auditControlId,
        audit_id: audit?.id,
        status: statusKey,
      };

      const result = await updateControlStatus({
        variables,
      });

      if (result?.data?.updateAuditControlStatus?.success) {
        setCurrentStatusKey(statusKey);
      } else {
        setCurrentStatusKey(originalStatus);
        const errorMessage = result?.data?.updateAuditControlStatus?.message || "Failed to update status";
        console.error("Failed to update status:", errorMessage);
        if (setSnackbarInfo) {
          setSnackbarInfo({
            message: errorMessage,
            vertical: "top",
            horizontal: "right",
          });
        }
      }
    } catch (error) {
      console.error("Error updating status:", error);
      setCurrentStatusKey(originalStatus);
      if (setSnackbarInfo) {
        setSnackbarInfo({
          message: error.message || "Error updating status",
          vertical: "top",
          horizontal: "right",
        });
      }
    } finally {
      setIsSubmittingStatus(false);
    }
  };

  const handleRedFlagToggle = async () => {
    if (!control?.auditControlId) return;
    setIsSubmittingRedFlag(true);

    const originalRedFlag = getRedFlag();
    const newRedFlag = !originalRedFlag;

    try {
      const variables = {
        audit_control_id: control.auditControlId,
        audit_id: audit?.id,
        red_flag: newRedFlag,
      };

      const result = await updateControlStatus({
        variables,
      });

      if (result?.data?.updateAuditControlStatus?.success) {
        setRedFlag(newRedFlag);
      } else {
        setRedFlag(originalRedFlag);
        const errorMessage = result?.data?.updateAuditControlStatus?.message || "Failed to update red flag";
        console.error("Failed to update red flag:", errorMessage);
        if (setSnackbarInfo) {
          setSnackbarInfo({
            message: errorMessage,
            vertical: "top",
            horizontal: "right",
          });
        }
      }
    } catch (error) {
      console.error("Error updating red flag:", error);
      setRedFlag(originalRedFlag);
      if (setSnackbarInfo) {
        setSnackbarInfo({
          message: error.message || "Error updating red flag",
          vertical: "top",
          horizontal: "right",
        });
      }
    } finally {
      setIsSubmittingRedFlag(false);
    }
  };

  const handleRemoveStatus = async () => {
    if (!control?.auditControlId) return;
    setIsSubmittingStatus(true);

    const originalStatus = getCurrentStatus();

    try {
      const newStatus = AdminStatusType.READY_FOR_AUDIT;
      const variables = {
        audit_control_id: control.auditControlId,
        audit_id: audit?.id,
        status: newStatus,
      };

      const result = await updateControlStatus({
        variables,
      });

      if (result?.data?.updateAuditControlStatus?.success) {
        setCurrentStatusKey(newStatus);
      } else {
        setCurrentStatusKey(originalStatus);
        const errorMessage = result?.data?.updateAuditControlStatus?.message || "Failed to remove status";
        console.error("Failed to remove status:", errorMessage);
        if (setSnackbarInfo) {
          setSnackbarInfo({
            message: errorMessage,
            vertical: "top",
            horizontal: "right",
          });
        }
      }
    } catch (error) {
      console.error("Error removing status:", error);
      setCurrentStatusKey(originalStatus);
      if (setSnackbarInfo) {
        setSnackbarInfo({
          message: error.message || "Error removing status",
          vertical: "top",
          horizontal: "right",
        });
      }
    } finally {
      setIsSubmittingStatus(false);
    }
  };

  return {
    currentStatusKey,
    redFlag,
    statusLoading: false,
    isSubmittingStatus,
    isSubmittingRedFlag,
    handleStatusUpdate,
    handleRedFlagToggle,
    handleRemoveStatus,
  };
};
